import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { scoreColor } from './uavsMeta';
import { useTokens } from '../primitives';

// Pick the longest non-empty improvement tip. Multiple queries often return
// near-duplicate tips from the same LLM; stacking them reads as a wall of
// repetitive text. Longest is a cheap proxy for "most informative".
function bestTip(queries) {
  let winner = '';
  for (const q of queries || []) {
    const tip = (q.improvementTip || q.whyNotRecommended || '').trim();
    if (tip.length > winner.length) winner = tip;
  }
  return winner;
}

function ProviderSubtitle({ provider }) {
  const { total, mentioned, recommended, mentionRate, topCompetitors } =
    provider;
  if (mentionRate === 0 && topCompetitors.length > 0) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {topCompetitors.length}{' '}
        {topCompetitors.length === 1 ? 'competitor' : 'competitors'} took your
        spot across {total} {total === 1 ? 'query' : 'queries'}
      </Typography>
    );
  }
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Mentioned in {mentioned} of {total} {total === 1 ? 'query' : 'queries'}
      {recommended > 0 && <> · recommended in {recommended}</>}
    </Typography>
  );
}

function CompetitorsBlock({ provider, leadCopy }) {
  if (provider.topCompetitors.length === 0) return null;
  return (
    <Box sx={{ mt: leadCopy ? 0 : 2 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          display: 'block',
          mb: 0.5,
        }}
      >
        {leadCopy || 'Most-mentioned competitors'}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {provider.topCompetitors.map((c) => (
          <Chip
            key={c.name}
            label={`${c.name} · ${c.mentionsAcrossQueries}`}
            size="small"
            sx={{ height: 22, fontSize: 12 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function QueriesBlock({ provider }) {
  const t = useTokens();
  if (provider.queries.length === 0) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          display: 'block',
          mb: 0.75,
        }}
      >
        Queries tested
      </Typography>
      <Stack spacing={0.75}>
        {provider.queries.map((q, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            {q.mentioned ? (
              <CheckCircleIcon
                sx={{ fontSize: 16, color: scoreColor(70, t), mt: 0.25 }}
              />
            ) : (
              <RadioButtonUncheckedIcon
                sx={{ fontSize: 16, color: 'text.disabled', mt: 0.25 }}
              />
            )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                &ldquo;{q.query}&rdquo;
              </Typography>
              {q.mentioned && q.recommended && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="success"
                  sx={{ mt: 0.25, height: 18, fontSize: 11 }}
                />
              )}
              {q.competitorNames?.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
                >
                  Competitors mentioned:{' '}
                  {q.competitorNames.slice(0, 4).join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function ProviderCard({ provider }) {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  const color = scoreColor(provider.mentionRate, t);
  const tip = bestTip(provider.queries);
  // When mention rate is low, lead with competitors — that's the most
  // actionable signal customers can do something about. At higher rates we
  // keep the queries-first ordering because it's the wins worth seeing.
  const competitorsFirst = provider.mentionRate < 40;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {provider.label}
            </Typography>
            <ProviderSubtitle provider={provider} />
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 600, color }}>
              {provider.mentionRate}%
            </Typography>
            <IconButton
              size="small"
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={provider.mentionRate}
          sx={{
            height: 5,
            borderRadius: 3,
            mt: 1.5,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          {competitorsFirst ? (
            <>
              <CompetitorsBlock
                provider={provider}
                leadCopy={
                  provider.mentionRate === 0
                    ? 'Brands AI recommended instead'
                    : 'Competitors taking your spot'
                }
              />
              <QueriesBlock provider={provider} />
            </>
          ) : (
            <>
              <QueriesBlock provider={provider} />
              <CompetitorsBlock provider={provider} />
            </>
          )}
          {tip && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: t.amberBg,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                How to take that spot
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                {tip}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function AIPresenceBreakdown({ aiPresence }) {
  if (!aiPresence || !aiPresence.providers || aiPresence.providers.length === 0)
    return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Where AI is talking about you
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        For each provider we ran representative queries about your business and
        recorded how often you came up — and who got mentioned instead when you
        didn&apos;t.
      </Typography>
      {aiPresence.providers.map((p) => (
        <ProviderCard key={p.provider} provider={p} />
      ))}
    </Box>
  );
}
